import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getBlogPost, BlogPost } from '../services/blog';
import { useViewTracking } from '../hooks/useViewTracking';

const BlogDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [blogPost, setBlogPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useViewTracking({ viewType: 'blog' });

  const loadBlogPost = useCallback(async (postId: string) => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading blog post with ID:', postId);
      
      const response = await getBlogPost(postId);
      
      if (!response || !response.data) {
        throw new Error('Invalid response from server');
      }
      
      console.log('Blog post loaded successfully:', response.data.title);
      setBlogPost(response.data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load blog post';
      console.error('Error loading blog post:', err);
      setError(errorMessage);
      setBlogPost(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Reset state when id changes
    setBlogPost(null);
    setError(null);
    setLoading(true);
    
    if (id) {
      loadBlogPost(id);
    } else {
      setLoading(false);
      setError('Invalid blog post ID');
    }
  }, [id, loadBlogPost]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <section className="py-20 brand-bg min-h-screen">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
            <p className="text-dark-600 dark:text-neutral-300">Loading blog post...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error || (!loading && !blogPost)) {
    return (
      <section className="py-20 brand-bg min-h-screen">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-500/10 border border-red-500/50 text-red-600 dark:text-red-400 rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Error Loading Blog Post</h2>
            <p className="mb-4 text-sm">{error || 'Blog post not found'}</p>
            {id && (
              <p className="mb-6 text-xs text-dark-500 dark:text-neutral-400">
                Post ID: {id}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => {
                  if (id) {
                    loadBlogPost(id);
                  }
                }}
                className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg"
              >
                Retry
              </button>
              <button
                onClick={() => navigate('/blog')}
                className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white font-semibold rounded-lg"
              >
                Back to Blog
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Type guard: ensure blogPost is not null before rendering
  if (!blogPost) {
    return null;
  }

  return (
    <section className="py-20 brand-bg min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate('/blog')}
          className="mb-6 text-primary-500 font-medium flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Blog
        </button>

        <article className="bg-white/80 dark:bg-dark-800/80 backdrop-blur-sm rounded-xl p-8 border border-primary-500/20">
            {blogPost.imageUrl && (
              <img
                src={blogPost.imageUrl}
                alt={blogPost.title}
                className="w-full h-auto max-h-[600px] object-contain rounded-lg mb-6"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            )}
            <div className="flex flex-wrap items-center gap-3 mb-4 text-sm text-dark-500 dark:text-neutral-400">
              {blogPost.category && (
                <span className="px-3 py-1 bg-primary-500/20 text-primary-600 dark:text-primary-400 rounded-full whitespace-nowrap">
                  {blogPost.category}
                </span>
              )}
              <span className="whitespace-nowrap">{formatDate(blogPost.publishedAt)}</span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <span>{blogPost.views} views</span>
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-dark-900 dark:text-neutral-50 mb-6">
              {blogPost.title}
            </h1>
            <div
              className="prose prose-lg dark:prose-invert max-w-none text-dark-700 dark:text-neutral-300 mb-8"
              dangerouslySetInnerHTML={{ __html: blogPost.content.replace(/\n/g, '<br />') }}
            />
            
            {/* WhatsApp CTA Button */}
            <div className="mt-8 pt-8 border-t border-primary-500/20">
              <div className="bg-gradient-to-r from-primary-500/10 to-primary-600/10 rounded-xl p-6 text-center">
                <h3 className="text-xl font-bold text-dark-900 dark:text-neutral-50 mb-3">
                  Ready to Start Your Project?
                </h3>
                <p className="text-dark-600 dark:text-neutral-300 mb-6">
                  Let's discuss how we can help bring your vision to life. Contact us on WhatsApp for a free consultation.
                </p>
                <a
                  href={`https://wa.me/2348145840203?text=${encodeURIComponent(`Hi! I just read your blog post: "${blogPost.title}". I'd like to learn more about your services.`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 px-6 py-3 bg-[#25D366] hover:bg-[#20BA5A] text-white font-semibold rounded-lg shadow-lg"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  <span>Chat with Us on WhatsApp</span>
                </a>
              </div>
            </div>
        </article>
      </div>
    </section>
  );
};

export default BlogDetailPage;

